import { createContext, useContext, useState, useCallback } from "react";

const HASH_KEY = "mos_lock_hash";
const SESSION_KEY = "mos_lock_ok";

async function sha256(str) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

const LockContext = createContext(null);

export function LockProvider({ children }) {
  const [pinSet, setPinSet] = useState(() => !!localStorage.getItem(HASH_KEY));
  const [locked, setLocked] = useState(
    () => !!localStorage.getItem(HASH_KEY) && sessionStorage.getItem(SESSION_KEY) !== "1"
  );
  const [settingsOpen, setSettingsOpen] = useState(false);

  const unlock = useCallback(async (pin) => {
    const stored = localStorage.getItem(HASH_KEY);
    if (!stored) return true;
    const ok = (await sha256(pin)) === stored;
    if (ok) {
      sessionStorage.setItem(SESSION_KEY, "1");
      setLocked(false);
    }
    return ok;
  }, []);

  const savePin = useCallback(async (pin) => {
    localStorage.setItem(HASH_KEY, await sha256(pin));
    sessionStorage.setItem(SESSION_KEY, "1");
    setPinSet(true);
    setLocked(false);
  }, []);

  const removePin = useCallback(() => {
    localStorage.removeItem(HASH_KEY);
    sessionStorage.removeItem(SESSION_KEY);
    setPinSet(false);
    setLocked(false);
  }, []);

  const lockNow = useCallback(() => {
    if (!localStorage.getItem(HASH_KEY)) return;
    sessionStorage.removeItem(SESSION_KEY);
    setLocked(true);
  }, []);

  return (
    <LockContext.Provider
      value={{
        pinSet,
        locked,
        settingsOpen,
        openSettings: () => setSettingsOpen(true),
        closeSettings: () => setSettingsOpen(false),
        unlock,
        savePin,
        removePin,
        lockNow,
      }}
    >
      {children}
    </LockContext.Provider>
  );
}

export const useLock = () => useContext(LockContext);
